import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import InvoicePrintModal from '../../components/InvoicePrintModal';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';

const SaleList = () => {
  const toast = useToast();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(`/sales?search=${search}&fromDate=${fromDate}&toDate=${toDate}`);
      if (res.success) {
        setSales(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchSales();
  };

  const handlePrintPreview = async (id) => {
    try {
      const res = await axiosClient.get(`/sales/${id}`);
      if (res.success) {
        setSelectedInvoice(res.data);
        setShowPrintModal(true);
      }
    } catch (err) {
      toast.error(err.message || 'Error loading sale invoice.');
    }
  };

  const handleDelete = async (id, saleNo) => {
    if (window.confirm(`Are you sure you want to delete Sale Bill #${saleNo}? This will also reverse the ledger posting.`)) {
      try {
        await axiosClient.delete(`/sales/${id}`);
        toast.success(`Sale Bill #${saleNo} deleted successfully.`);
        fetchSales();
      } catch (err) {
        toast.error(err.message || 'Error deleting sale invoice.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-receipt-cutoff text-primary me-2"></i> Sales Invoices History
          </h3>
          <p className="text-muted small mb-0">Search, view, print, or manage customer sale invoices</p>
        </div>
        <Link to="/sales/new" className="btn btn-ocean d-flex align-items-center gap-2">
          <i className="bi bi-cart-plus-fill"></i> Create New Sale
        </Link>
      </div>

      {/* Filter and Search Bar */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <form className="row g-2 align-items-center" onSubmit={handleSearchSubmit}>
            <div className="col-12 col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customer name, bill #, vehicle..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                placeholder="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <input
                type="date"
                className="form-control"
                placeholder="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-6 col-md-2">
              <button type="submit" className="btn btn-primary w-100">Filter</button>
            </div>

            <div className="col-6 col-md-2 text-end">
              <span className="badge bg-light text-dark border p-2">Total Bills: {sales.length}</span>
            </div>
          </form>
        </div>
      </div>

      {/* Sales List Table */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>Bill #</th>
                  <th>Date</th>
                  <th>Customer Name</th>
                  <th>Vehicle No</th>
                  <th className="text-center">Items</th>
                  <th className="text-end">Grand Total</th>
                  <th className="text-end">Paid Amt</th>
                  <th className="text-center" style={{ width: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      No sales records found.
                    </td>
                  </tr>
                ) : (
                  sales.map((s) => (
                    <tr key={s.Id}>
                      <td>
                        <strong className="text-primary">#{s.SaleNo}</strong>
                      </td>
                      <td>{formatDate(s.SaleDate)}</td>
                      <td>
                        <strong className="text-dark">{s.CustomerName || 'Cash Customer'}</strong>
                        {s.CustomerMobile && <div className="small text-muted"><i className="bi bi-telephone me-1"></i>{s.CustomerMobile}</div>}
                      </td>
                      <td>{s.VehicleNo || '-'}</td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark border">{s.itemCount} items</span>
                      </td>
                      <td className="text-end fw-bold text-dark">
                        ₹{parseFloat(s.GrandTotal).toFixed(2)}
                      </td>
                      <td className="text-end text-success fw-semibold">
                        ₹{parseFloat(s.PaidAmt).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-success"
                            onClick={() => handlePrintPreview(s.Id)}
                            title="Share on WhatsApp / PDF"
                          >
                            <i className="bi bi-whatsapp"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handlePrintPreview(s.Id)}
                            title="Print Invoice / Download PDF"
                          >
                            <i className="bi bi-printer"></i>
                          </button>
                          <Link
                            to={`/sales/edit/${s.Id}`}
                            className="btn btn-outline-primary"
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(s.Id, s.SaleNo)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Printable Invoice Modal */}
      {showPrintModal && (
        <InvoicePrintModal
          show={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          invoiceData={selectedInvoice}
          type="sale"
        />
      )}
    </div>
  );
};

export default SaleList;
